import { Button, Flex, Tooltip } from '@/components/ui';

import React, { useState } from 'react';

import { IconCheck, IconCopy } from '@tabler/icons-react';

interface CopyToClipboardProps {
  /** The value to copy to clipboard */
  value: string;
  /** Content to display (usually the text that represents the value) */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Size of the copy button */
  buttonSize?: 'xsmall' | 'small' | 'medium' | 'large';
  /** Whether to show success message in tooltip */
  showSuccessMessage?: boolean;
  /** Custom success message */
  successMessage?: string;
  /** Callback when copy is successful */
  onCopy?: (value: string) => void;
}

/**
 * CopyToClipboard component
 *
 * Wraps content with a copy button that copies the specified value to clipboard.
 * The copy button is hidden by default and appears on hover.
 * Shows success feedback with icon change and tooltip message.
 *
 * @example
 * <CopyToClipboard value="/path/to/file">
 *   /path/to/folder
 * </CopyToClipboard>
 */
export const CopyToClipboard: React.FC<CopyToClipboardProps> = ({
  value,
  children,
  className,
  buttonSize = 'xsmall',
  showSuccessMessage = true,
  successMessage = 'Copied to clipboard',
  onCopy,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      onCopy?.(value);

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      // Best-effort: clipboard access can be blocked by browser permissions.
    }
  };

  return (
    <Flex
      gap="2"
      align="center"
      className={`copy-to-clipboard ${className || ''}`}
    >
      {children && (
        <span className="copy-to-clipboard__content">{children}</span>
      )}
      <Tooltip
        content={
          copied && showSuccessMessage ? successMessage : 'Copy to clipboard'
        }
      >
        <Button
          variant="ghost"
          size={buttonSize}
          isIconOnly
          onClick={handleCopy}
          startIcon={copied ? <IconCheck /> : <IconCopy />}
          color={copied ? 'success' : 'default'}
          className="copy-to-clipboard__button"
          data-copied={copied}
        />
      </Tooltip>
    </Flex>
  );
};
